<template>
    <div class="fw_room_popup" role="dialog" aria-modal="true" aria-labelledby="forward-message-title"
        @click.self="forwardMsgPopupClosed">
        <div class="fw_room_center">
            <div class="fw_room_body">
                <div class="fw_room_header">
                    <h3 id="forward-message-title" class="fw_room_heading">
                        <button type="button" class="fw_room_close_button mr-3" aria-label="Close forward message popup"
                            @click="forwardMsgPopupClosed">
                            <i class="fa fa-times" aria-hidden="true" />
                        </button>

                        Forward message to
                    </h3>

                    <div class="fw_room_searchbar">
                        <div class="fw_room_searchbar_new_chat" :class="{ search_enabled: searchEnabled }">
                            <div class="search_for_new_chat_inner">
                                <button v-if="searchEnabled" type="button" class="back fw_search_action"
                                    aria-label="Clear search" @click="clearSearch">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"
                                        aria-hidden="true">
                                        <path fill="#87cefa"
                                            d="M12 4l1.4 1.4L7.8 11H20v2H7.8l5.6 5.6L12 20l-8-8 8-8z" />
                                    </svg>
                                </button>

                                <span class="search">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"
                                        aria-hidden="true">
                                        <path fill="#ffffff"
                                            d="M15.009 13.805h-.636l-.22-.219a5.184 5.184 0 0 0 1.256-3.386 5.207 5.207 0 1 0-5.207 5.208 5.183 5.183 0 0 0 3.385-1.255l.221.22v.635l4.004 3.999 1.194-1.195-3.997-4.007zm-4.808 0a3.605 3.605 0 1 1 0-7.21 3.605 3.605 0 0 1 0 7.21z" />
                                    </svg>
                                </span>

                                <input v-model="searchTerm" class="form-control" type="text"
                                    placeholder="Search or start new chat" aria-label="Search conversations"
                                    autocomplete="off" @focus="searchEnabled = true" @input="handleSearchInput">

                                <button v-if="searchTerm" type="button" class="x-alt fw_search_action"
                                    aria-label="Clear search" @click="clearSearch">
                                    <img src="@/assets/images/close.svg" class="img-fluid" alt="">
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="forward_popup_body">
                    <div class="fw_room_to_list_wrp">
                        <div class="fw_room_to_list" :aria-busy="searchLoading ? 'true' : 'false'" aria-live="polite">
                            <div v-if="searchLoading" class="fw_room_empty_state">
                                Searching...
                            </div>

                            <div v-else-if="availableRooms.length === 0" class="fw_room_empty_state">
                                {{
                                    isSearchMode
                                        ? 'No conversations found'
                                        : 'No conversations available'
                                }}
                            </div>

                            <template v-else>
                                <div v-for="(item, index) in availableRooms" :key="getRoomKey(item)"
                                    class="fw_room_to_list_item">
                                    <label :for="getRoomDomId(item, index)">
                                        <span class="forward_user_checkbox">
                                            <input :id="getRoomDomId(item, index)" type="checkbox"
                                                :checked="isRoomSelected(item)" @change="
                                                    toggleRoomSelection(
                                                        item,
                                                        $event.target.checked
                                                    )
                                                    ">

                                            <span class="checkbox">
                                                <i class="fa fa-check" aria-hidden="true" />
                                            </span>
                                        </span>

                                        <span class="forward_user_avtaar">
                                            <img :src="getRoomAvatar(item)" :alt="getRoomName(item)" width="50"
                                                height="50" loading="lazy" @error="handleAvatarError">
                                        </span>

                                        <span class="forward_user_info">
                                            <span class="forward_user_name">
                                                {{ getRoomName(item) }}
                                            </span>

                                            <span v-if="getRoomEmail(item)" class="forward_user_email">
                                                {{ getRoomEmail(item) }}
                                            </span>
                                        </span>
                                    </label>
                                </div>
                            </template>
                        </div>
                    </div>

                    <div v-if="selectedRooms.length > 0" class="msg_forward_btn_wrp">
                        <div class="row align-items-center">
                            <div class="col-10 pr-0">
                                <ul class="msg_forward_to_selcted_wrp">
                                    <li v-for="(item, index) in selectedRooms" :key="getRoomKey(item)">
                                        <span>{{ getRoomName(item) }}</span>
                                        <i v-if="index < selectedRooms.length - 1" aria-hidden="true">,</i>
                                    </li>
                                </ul>
                            </div>

                            <div class="col-2 text-right ml-auto">
                                <button type="button" class="btn" :disabled="submitted ||
                                    selectedRooms.length === 0
                                    " aria-label="Forward selected messages" @click="forwardMsgToSelectedUserMethod">
                                    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
                                        <path fill="#ffffff"
                                            d="M1.101 21.757L23.8 12.028 1.101 2.3l.011 7.912 13.623 1.816-13.623 1.817-.011 7.912z" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { bus } from '@/main';
import { mapGetters } from 'vuex';
import toast from '@/services/toast';

const DEFAULT_AVATAR = require('@/assets/images/user_default.png');
const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 400;
const SOCKET_OPEN_STATE = 1;

export default {
    name: 'ForwardMessagePopup',

    props: {
        forwardMsgInfo: {
            type: Array,
            default() {
                return [];
            }
        },

        roomSocketUrl: {
            default: null,
            validator(socket) {
                return (
                    socket === null ||
                    (
                        typeof socket === 'object' &&
                        typeof socket.send === 'function' &&
                        typeof socket.readyState === 'number'
                    )
                );
            }
        },

        chatRoomInfo: {
            type: Object,
            default: null
        }
    },

    data() {
        return {
            defaultAvatar: DEFAULT_AVATAR,
            searchEnabled: false,
            searchTerm: '',
            searchResults: [],
            searchLoading: false,
            searchTimer: null,
            searchRequestId: 0,
            selectedRooms: [],
            submitted: false,
            isClosing: false
        };
    },

    computed: {
        ...mapGetters([
            'normalChatRoomsGetters',
            'pinnedChatRoomsGetters'
        ]),

        pinnedRooms() {
            return this.extractRoomResults(
                this.pinnedChatRoomsGetters
            );
        },

        normalRooms() {
            return this.extractRoomResults(
                this.normalChatRoomsGetters
            );
        },

        currentChatRoomId() {
            return this.getChatRoomId(this.chatRoomInfo);
        },

        allChatRooms() {
            return this.getUniqueAvailableRooms(
                this.pinnedRooms.concat(this.normalRooms)
            );
        },

        normalizedSearchTerm() {
            return String(this.searchTerm || '').trim();
        },

        isSearchMode() {
            return (
                this.normalizedSearchTerm.length >=
                MIN_SEARCH_LENGTH
            );
        },

        availableRooms() {
            if (this.isSearchMode) {
                return this.getUniqueAvailableRooms(
                    this.searchResults
                );
            }

            return this.allChatRooms;
        }
    },

    mounted() {
        document.body.classList.add(
            'body_right_scroll_remove'
        );
        window.addEventListener('keydown', this.handleWindowKeydown);

        if (this.allChatRooms.length === 0) {
            this.refreshChatRooms().catch(() => {
                toast.error(
                    'Unable to load conversations. Please try again.'
                );
            });
        }
    },

    beforeDestroy() {
        this.cancelPendingSearch();
        document.body.classList.remove(
            'body_right_scroll_remove'
        );
        window.removeEventListener(
            'keydown',
            this.handleWindowKeydown
        );
    },

    methods: {
        extractRoomResults(source) {
            if (Array.isArray(source)) {
                return source;
            }

            if (!source || typeof source !== 'object') {
                return [];
            }

            if (Array.isArray(source.results)) {
                return source.results;
            }

            if (Array.isArray(source.data)) {
                return source.data;
            }

            if (
                source.data &&
                Array.isArray(source.data.results)
            ) {
                return source.data.results;
            }

            return [];
        },

        extractSearchResults(response) {
            if (!response) {
                return [];
            }

            const responseData =
                response.data !== undefined
                    ? response.data
                    : response;

            return this.extractRoomResults(responseData);
        },

        isValidId(value) {
            return (
                value !== null &&
                value !== undefined &&
                String(value).trim() !== ''
            );
        },

        getChatRoomId(room) {
            if (!room || typeof room !== 'object') {
                return null;
            }

            if (this.isValidId(room.chat_room)) {
                return room.chat_room;
            }

            if (this.isValidId(room.chat_room_id)) {
                return room.chat_room_id;
            }

            return null;
        },

        getRoomKey(room) {
            return `room-${String(this.getChatRoomId(room))}`;
        },

        getRoomDomId(room, index) {
            const roomId = String(
                this.getChatRoomId(room) || index
            ).replace(/[^a-zA-Z0-9_-]/g, '-');

            return `forward-room-${index}-${roomId}`;
        },

        getUniqueAvailableRooms(rooms) {
            const seenRoomIds = Object.create(null);
            const currentRoomId = this.currentChatRoomId;

            return (Array.isArray(rooms) ? rooms : []).filter(
                (room) => {
                    const roomId = this.getChatRoomId(room);

                    if (!this.isValidId(roomId)) {
                        return false;
                    }

                    if (
                        this.isValidId(currentRoomId) &&
                        String(roomId) === String(currentRoomId)
                    ) {
                        return false;
                    }

                    const roomKey = String(roomId);

                    if (seenRoomIds[roomKey]) {
                        return false;
                    }

                    seenRoomIds[roomKey] = true;
                    return true;
                }
            );
        },

        getRoomUser(room) {
            if (!room || typeof room !== 'object') {
                return {};
            }

            return room.user && typeof room.user === 'object'
                ? room.user
                : {};
        },

        getRoomName(room) {
            if (!room || typeof room !== 'object') {
                return 'Unknown conversation';
            }

            if (room.is_group_chat) {
                return (
                    String(room.group_name || room.name || '').trim() ||
                    'Unnamed group'
                );
            }

            const user = this.getRoomUser(room);
            const fullName = [
                user.first_name,
                user.last_name
            ]
                .filter(Boolean)
                .join(' ')
                .trim();

            return (
                fullName ||
                user.email ||
                user.handle ||
                'Unknown user'
            );
        },

        getRoomEmail(room) {
            if (!room || room.is_group_chat) {
                return '';
            }

            return this.getRoomUser(room).email || '';
        },

        getRoomAvatar(room) {
            if (!room || typeof room !== 'object') {
                return this.defaultAvatar;
            }

            if (room.is_group_chat) {
                return room.group_image || this.defaultAvatar;
            }

            const user = this.getRoomUser(room);
            const profile =
                user.profile && typeof user.profile === 'object'
                    ? user.profile
                    : {};

            return (
                profile.thumbnail ||
                user.thumbnail ||
                this.defaultAvatar
            );
        },

        handleAvatarError(event) {
            const image = event && event.target;

            if (!image) {
                return;
            }

            image.onerror = null;
            image.src = this.defaultAvatar;
        },

        isRoomSelected(room) {
            const roomId = this.getChatRoomId(room);

            return this.selectedRooms.some((selectedRoom) => {
                return (
                    String(this.getChatRoomId(selectedRoom)) ===
                    String(roomId)
                );
            });
        },

        toggleRoomSelection(room, selected) {
            const roomId = this.getChatRoomId(room);

            if (!this.isValidId(roomId)) {
                return;
            }

            const selectedIndex = this.selectedRooms.findIndex(
                (selectedRoom) => {
                    return (
                        String(
                            this.getChatRoomId(selectedRoom)
                        ) === String(roomId)
                    );
                }
            );

            if (selected && selectedIndex === -1) {
                this.selectedRooms.push(room);
            } else if (!selected && selectedIndex !== -1) {
                this.selectedRooms.splice(selectedIndex, 1);
            }
        },

        cancelPendingSearch() {
            if (this.searchTimer) {
                clearTimeout(this.searchTimer);
                this.searchTimer = null;
            }

            this.searchRequestId += 1;
            this.searchLoading = false;
        },

        clearSearch() {
            this.cancelPendingSearch();
            this.searchEnabled = false;
            this.searchTerm = '';
            this.searchResults = [];
        },

        handleSearchInput() {
            this.searchEnabled = true;
            this.cancelPendingSearch();
            this.searchResults = [];

            const query = this.normalizedSearchTerm;

            if (query.length < MIN_SEARCH_LENGTH) {
                return;
            }

            const requestId = this.searchRequestId;

            this.searchLoading = true;
            this.searchTimer = setTimeout(() => {
                this.searchTimer = null;
                this.searchUsers(query, requestId);
            }, SEARCH_DEBOUNCE_MS);
        },

        searchUsers(query, requestId) {
            this.$store
                .dispatch('searchWorkspaceUser', { search_term: query })
                .then((response) => {
                    if (requestId !== this.searchRequestId) {
                        return;
                    }

                    this.searchResults =
                        this.extractSearchResults(response);
                })
                .catch(() => {
                    if (requestId !== this.searchRequestId) {
                        return;
                    }

                    this.searchResults = [];
                    toast.error(
                        'Unable to search conversations. Please try again.'
                    );
                })
                .then(() => {
                    if (requestId === this.searchRequestId) {
                        this.searchLoading = false;
                    }
                });
        },

        handleWindowKeydown(event) {
            if (event && event.key === 'Escape') {
                this.forwardMsgPopupClosed();
            }
        },

        forwardMsgPopupClosed() {
            if (this.isClosing) {
                return;
            }

            this.isClosing = true;
            this.cancelPendingSearch();
            document.body.classList.remove(
                'body_right_scroll_remove'
            );
            
            bus.$emit('forwardMsgPopupBus', false);
        },

        getMessageId(message) {
            if (!message || typeof message !== 'object') {
                return null;
            }

            let messageData = message;

            if (
                message.chat_data &&
                typeof message.chat_data === 'object'
            ) {
                messageData = message.chat_data;
            } else if (
                message.message &&
                typeof message.message === 'object'
            ) {
                messageData = message.message;
            }

            if (this.isValidId(messageData.id)) {
                return messageData.id;
            }

            if (this.isValidId(messageData.message_id)) {
                return messageData.message_id;
            }

            return null;
        },

        getForwardedMessageIds() {
            const seenIds = Object.create(null);

            return this.forwardMsgInfo.reduce(
                (messageIds, message) => {
                    const messageId = this.getMessageId(message);

                    if (!this.isValidId(messageId)) {
                        return messageIds;
                    }

                    const messageKey = String(messageId);

                    if (!seenIds[messageKey]) {
                        seenIds[messageKey] = true;
                        messageIds.push(messageId);
                    }

                    return messageIds;
                },
                []
            );
        },

        getSelectedRoomIds() {
            const seenIds = Object.create(null);

            return this.selectedRooms.reduce(
                (roomIds, room) => {
                    const roomId = this.getChatRoomId(room);

                    if (!this.isValidId(roomId)) {
                        return roomIds;
                    }

                    const roomKey = String(roomId);

                    if (!seenIds[roomKey]) {
                        seenIds[roomKey] = true;
                        roomIds.push(roomId);
                    }

                    return roomIds;
                },
                []
            );
        },

        isSocketReady() {
            return Boolean(
                this.roomSocketUrl &&
                typeof this.roomSocketUrl.send === 'function' &&
                (
                    this.roomSocketUrl.readyState === undefined ||
                    this.roomSocketUrl.readyState ===
                    SOCKET_OPEN_STATE
                )
            );
        },

        refreshChatRooms() {
            return Promise.all([
                this.$store.dispatch('fetchPinnedChatRooms'),
                this.$store.dispatch('fetchNormalChatRooms')
            ]);
        },

        forwardMsgToSelectedUserMethod() {
            if (this.submitted) {
                return;
            }

            const roomIds = this.getSelectedRoomIds();
            const messageIds = this.getForwardedMessageIds();

            if (roomIds.length === 0) {
                toast.error(
                    'Select at least one conversation.'
                );
                return;
            }

            if (messageIds.length === 0) {
                toast.error(
                    'No valid messages are available to forward.'
                );
                return;
            }

            if (!this.isSocketReady()) {
                toast.error(
                    'Chat is reconnecting. Please try again in a moment.'
                );
                return;
            }

            this.submitted = true;

            try {
                this.roomSocketUrl.send(
                    JSON.stringify({
                        type: 'forward_message',
                        message_id: messageIds,
                        new_chat_room_id: roomIds
                    })
                );
                   this.refreshChatRooms().then(
                        () => {
                            const destinationRoom =
                            this.selectedRooms[this.selectedRooms.length - 1] || null;

                            this.$emit('forwarded', {
                                destinationRoom
                            });
                            this.forwardMsgPopupClosed();
                        },
                        () => {
                            // Forwarding already succeeded. Close even if refresh fails.
                            this.forwardMsgPopupClosed();
                        }
                    );
            } catch (error) {
                this.submitted = false;
                toast.error(
                    'The messages could not be forwarded. Please try again.'
                );
                return;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.fw_room_close_button,
.fw_search_action {
    padding: 0;
    color: inherit;
    cursor: pointer;
    background: transparent;
    border: 0;
}

.fw_room_close_button:focus,
.fw_search_action:focus {
    outline: 2px solid #87cefa;
    outline-offset: 2px;
}

.fw_room_empty_state {
    padding: 24px 15px;
    color: rgba(255, 255, 255, 0.65);
    text-align: center;
}

</style>


<style lang="scss" scoped>
.fw_room_popup {
    position: fixed;
    z-index: 1000;
    background: rgba(0, 0, 0, 0.92);
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;

    .fw_room_center {
        height: 100%;
        display: flex;
        width: 100%;
        align-items: center;
    }

    .fw_room_body {
        background: #000;
        max-width: 400px;
        width: 100%;
        margin: 0 auto;
        border-radius: 5px;
        -webkit-box-shadow: 3px 3px 3px rgba(255, 255, 255, 0.05), -3px -3px 3px rgba(255, 255, 255, 0.05);
        box-shadow: 3px 3px 3px rgba(255, 255, 255, 0.05), -3px -3px 3px rgba(255, 255, 255, 0.05);
    }

    .fw_room_heading {
        background: #232323;
        color: #ffffff;
        padding: 10px 15px;
        margin: 0;
        border-radius: 5px 5px 0px 0px;
        font-size: 22px;
        font-weight: normal;

        .fa {
            cursor: pointer;

            &:hover {
                color: #e4002b;
            }
        }
    }

    .fw_room_to_list_wrp {
        height: 280px;
        overflow-y: auto;
        padding: 10px 0;

        &::-webkit-scrollbar {
            width: 4px;
            background: transparent;
        }

        &::-webkit-scrollbar-thumb {
            background-color: #333333;
        }
    }

    .fw_room_to_list {
        .fw_room_to_list_item {
            padding: 0px 15px;

            .forward_user_checkbox {
                .checkbox {
                    display: -webkit-box;
                    display: -ms-flexbox;
                    display: flex;
                    width: 16px;
                    height: 16px;
                    border: 2px solid #ffc107;
                    border-radius: 2px;
                    line-height: 16px;
                    -webkit-box-pack: center;
                    -ms-flex-pack: center;
                    justify-content: center;
                    cursor: pointer;

                    .fa {
                        color: #000000;
                        font-size: 0px;
                    }
                }

                input[type="checkbox"] {
                    visibility: hidden;
                    display: none;

                    &:checked+.checkbox {
                        background-color: #ffc107;
                        border-color: #ffc107;

                        .fa {
                            font-size: 14px;
                            line-height: 1;
                        }
                    }
                }
            }
        }

        label {
            display: flex;
            align-items: center;
            cursor: pointer;
            margin: 0px;
        }

        .forward_user_avtaar {
            padding: 5px 10px;

            img {
                width: 40px;
                height: 40px;
                border-radius: 50%;
            }
        }

        .forward_user_info {
            span {
                display: block;
            }

            .forward_user_name {
                line-height: 1;
                font-size: 16px;
                margin-bottom: 3px;
                color: #fff;
            }

            .forward_user_email {
                font-size: 15px;
                color: rgba(255, 255, 255, 0.5);
                white-space: pre-wrap;
                word-break: break-all;
            }
        }
    }

    .fw_room_searchbar_new_chat {
        padding: 10px;
        background: #1d2226;
        border-bottom: 1px solid #171b1f;

        .search_for_new_chat_inner {
            position: relative;
            background: rgba(255, 255, 255, 0.1);
            border-radius: 80px;
            padding: 0;

            .back {
                display: none;
                position: absolute;
                left: 0px;
                top: 50%;
                transform: translateY(-50%);
                -webkit-transform: translateY(-50%);
                -moz-transform: translateY(-50%);
                cursor: pointer;
            }

            .search {
                position: absolute;
                left: 10px;
                top: 50%;
                transform: translateY(-50%);
                -webkit-transform: translateY(-50%);
                -moz-transform: translateY(-50%);
            }

            .x-alt {
                position: absolute;
                right: 15px;
                top: 50%;
                transform: translateY(-50%);
                -webkit-transform: translateY(-50%);
                -moz-transform: translateY(-50%);
                cursor: pointer;
                &:hover{
                    .img-fluid{filter:brightness(0) invert(1);}
                }

                .img-fluid {
                    width: 15px;
                }
            }

            .form-control {
                background: none;
                border: none;
                color: #ffffff;
                padding: 8px 45px;
                line-height: 1;
                height: auto;
                font-size: 16px;
                outline: none;
                box-shadow: none;

                &::-moz-placeholder {
                    color: rgba(255, 255, 255, 0.6);
                }

                &::-webkit-input-placeholder {
                    color: rgba(255, 255, 255, 0.6);
                }

                &:focus::-webkit-input-placeholder {
                    font-size: 0;
                }

                &:focus::-moz-placeholder {
                    font-size: 0;
                }
            }

            .x-alt.search_disabled_btn {
                display: block;
            }
        }

        &.search_enabled {
            background: rgba(255, 255, 255, 0.1);

            .search_for_new_chat_inner {
                background: none;

                .search {
                    display: none;
                }

                .back {
                    display: block;
                    &:hover{
                        path{fill: #ffc107}
                    }
                }
            }
        }
    }

    .msg_forward_btn_wrp {
        background: #87cefa;
        color: #000;
        padding: 5px 15px;
        border-radius: 0px 0px 5px 5px;
        // position: absolute;
        width: 100%;

        // bottom: 0;
        .msg_forward_to_selcted_wrp {
            padding: 0;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            // width: 85%;
            margin: 0px;

            li {
                font-size: 16px;
                margin-right: 5px;

                &:last-child i {
                    display: none;
                }

                display:inline-block;
            }
        }

        .btn {
            padding: 0 0 0 2px;
            outline: none;
            -webkit-box-shadow: 0 1px 3px rgba(11, 20, 26, .4);
            box-shadow: 0 1px 3px rgba(11, 20, 26, .4);
            background: #000;
            border-radius: 50%;
            height: 40px;
            width: 40px;
            text-align: center;
            line-height: 20px;

            &:hover {
                background: #ffe400;

                svg path {
                    fill: #000000;
                }
            }
            &:disabled {
                cursor: not-allowed;
                opacity: 0.55;
            }
        }
    }

    .forward_popup_body {
        position: relative;
    }
}
</style>